import { useEffect, useState } from 'react';
import { ArrowRightLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { useContactSummary } from '@/hooks/contacts/useContactSummary';
import { useApplyCredit } from '@/hooks/invoices/useApplyCredit';

interface ApplyCreditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  invoiceId: string;
  invoiceNumber?: string | null;
  balanceDue: number;
  /**
   * Credit known by the caller (e.g. from the contact summary already loaded on
   * the invoice page). When omitted the modal fetches it from the contact
   * summary itself.
   */
  creditAvailable?: number;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n);
}

export function ApplyCreditModal({
  open,
  onOpenChange,
  contactId,
  invoiceId,
  invoiceNumber,
  balanceDue,
  creditAvailable: creditAvailableProp,
}: ApplyCreditModalProps) {
  const mutation = useApplyCredit(contactId);
  const [amount, setAmount] = useState('');
  const [amountDirty, setAmountDirty] = useState(false);

  // Fall back to fetching the contact's credit when the caller didn't pass it,
  // and refresh it while the modal is open so it tracks recent applications.
  const summaryQuery = useContactSummary(contactId);
  const creditAvailable = creditAvailableProp ?? Number(summaryQuery.data?.creditAvailable ?? 0);

  const maxApply = Math.min(creditAvailable, balanceDue);
  const numericAmount = Number(amount) || 0;

  useEffect(() => {
    if (!open) {
      setAmount('');
      setAmountDirty(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || amountDirty || maxApply <= 0) return;
    setAmount(maxApply.toFixed(2));
  }, [open, amountDirty, maxApply, invoiceId]);

  const amountError =
    open && maxApply > 0 && (numericAmount <= 0 || numericAmount > maxApply)
      ? `Enter an amount between $0.01 and ${formatCurrency(maxApply)}`
      : null;

  const canSubmit = numericAmount > 0 && numericAmount <= maxApply + 0.001 && !mutation.isPending;

  function handleSubmit() {
    if (numericAmount <= 0) return;
    mutation.mutate(
      { contactId, invoiceId, amount: numericAmount },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  const label = invoiceNumber ? `#${invoiceNumber}` : 'unavailable invoice';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="size-4" />
            Apply payment credit
          </DialogTitle>
          <DialogDescription>
            {formatCurrency(creditAvailable)} of unapplied payment credit available for invoice{' '}
            {label} ({formatCurrency(balanceDue)} due).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="apply-credit-amount">Amount</Label>
            <Input
              id="apply-credit-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={maxApply}
              placeholder={maxApply.toFixed(2)}
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setAmountDirty(true);
              }}
            />
            {amountError ? <p className="text-xs text-destructive">{amountError}</p> : null}
            <button
              type="button"
              onClick={() => {
                setAmount(maxApply.toFixed(2));
                setAmountDirty(true);
              }}
              disabled={maxApply <= 0}
              className="text-xs font-medium text-brand-blue hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Apply max: {formatCurrency(maxApply)}
            </button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {mutation.isPending ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
            Apply payment credit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
